import classNames from 'classnames';
import {CreditCardType} from 'components/CreditCardType';
import FormGroup from 'components/FormGroup';
import TextField from 'components/TextField';
import {CardType, getCardType} from 'helpers/getCardTypeIcon';
import {Controller, UseFormReturn} from 'react-hook-form';
import {Address, createCard} from 'types/order';
import styles from './CardInformation.module.scss';
import SelectField from 'components/SelectField';
import Button from 'components/Button';
import ReactSwitch from 'react-switch';

type FormData = Omit<createCard, 'label'>;

type Props = {
  onSuccess: (data: FormData) => void;
  addresses: Address[];
  onAddAddress: () => void;
  isPrimary: boolean;
  setIsPrimary: (val: boolean) => void;
  loading: boolean;
  form: UseFormReturn<FormData>;
};

export function cc_format(value: string) {
  var v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  var matches = v.match(/\d{4,16}/g);
  var match = (matches && matches[0]) || '';
  var parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  if (parts.length) {
    return parts.join(' ');
  } else {
    return value;
  }
}
export function dd_format(value: string, previousValue: string = '') {
  // Remove all non-numeric characters
  let v = value.replace(/[^0-9]/gi, '');

  // If empty, return empty
  if (!v) return '';

  // Get first digit
  const firstDigit = parseInt(v[0]);

  // If first digit is 2-9, prepend 0 and add separator
  if (v.length === 1 && firstDigit >= 2 && firstDigit <= 9) {
    return '0' + v + '/';
  }

  // If we have at least 2 digits
  if (v.length >= 2) {
    let month = v.substring(0, 2);
    let monthNum = parseInt(month);
    let year = '';

    // If first digit is 0 or 1 but month > 12, reformat
    // e.g., "15" becomes "01/5", "19" becomes "01/9"
    if (monthNum > 12) {
      month = '0' + v[0];
      // Rest of the digits become the year
      year = v.substring(1, 3);
      return month + '/' + year;
    }

    // If we only have 2 digits (month only)
    if (v.length === 2) {
      // Auto-add separator after valid month when typing forward
      if (
        monthNum >= 1 &&
        monthNum <= 12 &&
        previousValue.length < value.length
      ) {
        return month + '/';
      }
      return month;
    }

    // Format as MM/YY (3 or more digits)
    year = v.substring(2, 4);
    return month + '/' + year;
  }

  return v;
}

function HeartlandForm({
  onSuccess,
  addresses,
  onAddAddress,
  isPrimary,
  setIsPrimary,
  loading,
  form,
}: Props) {
  const {control, setValue, handleSubmit, watch, setError, clearErrors} = form;

  return (
    <>
      <div className={styles.modalWrapper}>
        <div className={styles.cardTypeWrapper}>
          <div
            className={classNames(
              styles.cardType,
              getCardType(watch('card_number') || '') && styles.cardTypeVisible
            )}
          >
            {getCardType(watch('card_number') || '') && (
              <CreditCardType
                type={getCardType(watch('card_number')) as CardType}
              />
            )}
          </div>
          <FormGroup>
            <Controller
              name="card_number"
              control={control}
              rules={{
                onChange: (e) => {
                  const value = cc_format(e.target.value);
                  if (value && value.length === 18 && !getCardType(value)) {
                    setError('card_number', {
                      message: 'Invalid card number',
                    });
                  } else {
                    clearErrors('card_number');
                  }
                },
                required: 'Please, fill card number',
                minLength: {
                  value: 18,
                  message: 'Please, fill card number',
                },
              }}
              render={({field, fieldState}) => (
                <TextField
                  label="Card Number"
                  onChange={(event) =>
                    field.onChange(cc_format(event.target.value))
                  }
                  onBlur={field.onBlur}
                  wrapperClass={styles.googleInput}
                  placeholder="**** **** **** 1234"
                  value={field.value}
                  error={fieldState.error?.message}
                />
              )}
            />
          </FormGroup>
        </div>

        <FormGroup columns={2} className={styles.modalWrapper}>
          <Controller
            name="expiry_date"
            control={control}
            rules={{
              required: 'Please, fill expiry date',
              minLength: {
                value: 5,
                message: 'Please, fill expiry date',
              },
            }}
            render={({field, fieldState}) => (
              <TextField
                label="Expiry date"
                wrapperClass={styles.googleInput}
                placeholder="mm/yy"
                hasError={false}
                onChange={(event) => {
                  setValue('expiry_date', dd_format(event.target.value));
                }}
                value={field.value}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="secure_code"
            control={control}
            rules={{
              required: 'Please, fill secure code',
              minLength: {
                value: 3,
                message: 'Please, fill secure code',
              },
            }}
            render={({field, fieldState}) => (
              <TextField
                maxLength={4}
                label="Secure code"
                value={field.value}
                wrapperClass={styles.googleInput}
                placeholder="CVC"
                hasError={false}
                onChange={(event) => {
                  setValue(
                    'secure_code',
                    event.target.value.replace(/\D/g, '')
                  );
                }}
                error={fieldState.error?.message}
              />
            )}
          />
        </FormGroup>
        <FormGroup className={styles.switchForm}>
          <Controller
            name="holder_name"
            control={control}
            rules={{required: 'Please, fill holder name'}}
            render={({field, fieldState}) => (
              <TextField
                label="Card holder name"
                wrapperClass={styles.googleInput}
                placeholder="Enter card holder name"
                hasError={false}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </FormGroup>
        <FormGroup style={{position: 'relative', marginTop: 10}}>
          <Controller
            name="billing_address"
            control={control}
            rules={{required: 'Please select address'}}
            render={({field: {ref, ...field}, fieldState}) => (
              <SelectField
                {...field}
                label="Billing Address"
                placeholder="Select address"
                options={addresses
                  .filter((item) => item.type === 'billing')
                  .map((item) => ({
                    value: item.id,
                    label: `${item.address_line_1}, ${item.city}, ${item.state}, ${item.zipcode}`,
                  }))}
                error={fieldState.error?.message}
                value={field.value}
                onChange={(newValue) => {
                  if (newValue) {
                    setValue('billing_address', newValue);
                  }
                }}
              />
            )}
          />
          <Button
            className={styles.addAddressButton}
            onClick={onAddAddress}
            component="a"
          >
            +
          </Button>
        </FormGroup>
        <div className={styles.primaryCardWrapper}>
          <div>
            <p>Primary card:</p>
          </div>
          <ReactSwitch checked={isPrimary} onChange={setIsPrimary} />
        </div>
        <div className={styles.actionWrapper}>
          <Button
            loading={loading}
            type="submit"
            onClick={handleSubmit(onSuccess)}
          >
            Save
          </Button>
        </div>
      </div>
    </>
  );
}
export default HeartlandForm;
